import { env } from "@/app/config/env";
import { platform } from "@/app/platform";
import { scrub, scrubString } from "./scrub";

/**
 * THE REPORTING SEAM.
 *
 * Two rules, both from the audit that produced this design:
 *
 * 1. **Reporting is structural, not remembered.** Nothing in a feature calls this directly
 *    for a failed request: the query cache reports every failure and the service reports
 *    every rejected call. A new screen cannot ship unreported.
 * 2. **No provider is load-bearing.** Sentry and PostHog are behind this interface and are
 *    imported ONLY when their keys exist, so a project with no DSN pays nothing — no
 *    network, no bundle on the critical path — and swapping either one touches this folder.
 *
 * On a phone this matters more than on the web: there is no console to open, no network tab,
 * and the person who hit the bug is not going to file a useful report.
 */
type Context = Record<string, unknown>;

interface Reporter {
  error(error: unknown, context?: Context): void;
  message(message: string, context?: Context): void;
  identify(userId: string | null): void;
  track(event: string, properties?: Context): void;
}

const consoleReporter: Reporter = {
  error(error, context) {
    console.error("[report]", error, context ? scrub(context) : "");
  },
  message(message, context) {
    console.warn("[report]", scrubString(message), context ? scrub(context) : "");
  },
  identify() {},
  track(event, properties) {
    if (!env.isProduction) console.info("[track]", event, properties ? scrub(properties) : "");
  },
};

let reporter: Reporter = consoleReporter;

/**
 * Starts whatever is configured. Safe to call when nothing is: the console reporter stays,
 * which is the honest behaviour for a dev build.
 */
export async function initObservability(): Promise<void> {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const posthogKey = import.meta.env.VITE_POSTHOG_KEY;
  if (!dsn && !posthogKey) return;

  const [sentry, posthog] = await Promise.all([
    dsn ? import("@sentry/react") : Promise.resolve(null),
    posthogKey ? import("posthog-js") : Promise.resolve(null),
  ]);

  sentry?.init({
    dsn,
    // The release is what makes a report actionable: an installed app can be any version,
    // and "which build was this?" is otherwise unanswerable.
    release: `${env.appName}@${env.version}`,
    environment: env.appEnv,
    tracesSampleRate: 0,
    beforeSend(event) {
      return scrub(event) as typeof event;
    },
    initialScope: { tags: { platform, appEnv: env.appEnv } },
  });

  posthog?.default.init(posthogKey!, {
    api_host: import.meta.env.VITE_POSTHOG_HOST ?? "https://eu.i.posthog.com",
    autocapture: false,
    capture_pageview: false,
    // A phone app is not a website: `person_profiles` on every anonymous launch inflates
    // the bill and tells you nothing until someone signs in.
    person_profiles: "identified_only",
  });

  reporter = {
    error(error, context) {
      consoleReporter.error(error, context);
      sentry?.captureException(error, { extra: scrub(context ?? {}) as Context });
    },
    message(message, context) {
      sentry?.captureMessage(scrubString(message), { extra: scrub(context ?? {}) as Context });
    },
    identify(userId) {
      sentry?.setUser(userId ? { id: userId } : null);
      if (userId) posthog?.default.identify(userId);
      else posthog?.default.reset();
    },
    track(event, properties) {
      posthog?.default.capture(event, scrub(properties ?? {}) as Context);
    },
  };
}

export function reportError(error: unknown, context?: Context): void {
  reporter.error(error, context);
}

export function reportMessage(message: string, context?: Context): void {
  reporter.message(message, context);
}

/** Ties reports to a user without ever sending their email or name. */
export function identifyUser(userId: string | null): void {
  reporter.identify(userId);
}

/**
 * Product events. Funnels only — the steps of a flow that matters, entered / completed /
 * abandoned. Not every tap: an event nobody has a question about is a cost with no answer.
 */
export function track(event: string, properties?: Context): void {
  reporter.track(event, properties);
}
